import { useEffect, useState } from 'react';
import { tenantStore } from '../lib/tenant-store';
import { api, type Job } from '../api-client';

export default function Dashboard() {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);

  useEffect(() => {
    const id = tenantStore.get();
    setTenantId(id);
    if (id) api.recentJobs(id).then((r) => setJobs(r.jobs)).catch(() => {});
  }, []);

  if (!tenantId) {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-4">ようこそ</h2>
        <p className="text-slate-600">
          まず「テナント設定」ページからテナントを作成してください。
        </p>
      </div>
    );
  }

  const successCount = jobs.filter((j) => j.status === 'success').length;
  const failedCount = jobs.filter((j) => j.status === 'failed').length;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">ダッシュボード</h2>
      <p className="text-sm text-slate-500 mb-6">Tenant: {tenantId}</p>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <StatCard label="直近ジョブ数" value={jobs.length} />
        <StatCard label="成功" value={successCount} color="text-green-600" />
        <StatCard label="失敗" value={failedCount} color="text-red-600" />
      </div>

      <h3 className="text-lg font-semibold mb-3">最近のジョブ</h3>
      <div className="bg-white rounded shadow border">
        {jobs.length === 0 ? (
          <p className="p-4 text-slate-500 text-sm">まだジョブがありません</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">状態</th>
                <th className="px-3 py-2">行数</th>
                <th className="px-3 py-2">実行時刻</th>
              </tr>
            </thead>
            <tbody>
              {jobs.slice(0, 10).map((j) => (
                <tr key={j.id} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs">{j.id.slice(0, 8)}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={j.status} />
                  </td>
                  <td className="px-3 py-2">{j.row_count ?? '-'}</td>
                  <td className="px-3 py-2 text-slate-500">
                    {new Date(j.started_at).toLocaleString('ja-JP')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color = 'text-slate-900' }: { label: string; value: number; color?: string }) {
  return (
    <div className="bg-white rounded shadow border p-4">
      <p className="text-sm text-slate-500">{label}</p>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    success: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    running: 'bg-blue-100 text-blue-800',
    queued: 'bg-slate-100 text-slate-800',
  };
  return <span className={`px-2 py-0.5 text-xs rounded ${map[status] ?? ''}`}>{status}</span>;
}
